import { ApiError } from '@/api/client';

/**
 * Query options for LIVE money / platform reads (CP2).
 *
 * - `staleTime: 0` + `refetchOnMount: 'always'` — every visit asks the owning service again.
 * - `gcTime: 0` — once a page unmounts its figures are dropped from the browser cache, so returning to it
 *   shows a spinner and a fresh answer, never a remembered balance or price.
 * - `retry: false` — a 503 is an answer (the labelled panel), not something to hammer.
 *
 * Paired with {@link QueryState}, which renders the error before any data: react-query keeps the last
 * successful data when a refetch fails, and showing it next to a failed live call would be exactly the
 * stale money figure CP2 forbids.
 */
export const LIVE = {
  staleTime: 0,
  gcTime: 0,
  retry: false,
  refetchOnMount: 'always' as const,
  refetchOnWindowFocus: false,
};

/** Replica reads (environments, tenants, footprint, jobs): cheap, no money, the default freshness. */
export const REPLICA = {
  retry: false,
};

/** True when the error is the labelled "unavailable" answer (503) of a live read. */
export function isUnavailable(error: unknown): error is ApiError {
  return error instanceof ApiError && error.isUnavailable;
}

/** The application code of a failed call, if the server sent one. */
export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}
